/**
 * The create-user form — `/countries/:cc/admin/users/new`. AUTHORED.
 *
 * ===========================================================================
 * D5, AS FORM RULES — the client mirrors `assertInitialRoleAssignments`
 * ===========================================================================
 * · INITIAL ROLES ARE REQUIRED for every ordinary account: a country-scoped
 *   `users.manage` holder "may create users provided the request includes
 *   initial role assignments" (d5-boundary.ts:73). The form refuses to submit
 *   without one and says why, BEFORE the round trip — the server still
 *   enforces it (`VALIDATION_FAILED {field:'roles'}`).
 * · THE COUNTRY OPTIONS ARE THE CREATOR'S MANAGEABLE SET — every assignment
 *   must be in the creator's scope, so out-of-scope countries are simply not
 *   offered (see `role-editor.tsx`).
 * · THE GROUP-ADMIN SWITCH RENDERS ONLY FOR grp CALLERS. Creating another
 *   group admin is `grp:true`-only server-side; a visible-but-refused checkbox
 *   would advertise a capability the caller does not have (the D5 "the UI
 *   hides what the caller cannot do" rule — asserted by `users-admin.test`).
 *   When it is checked, roles become optional: spec §10 group-admin semantics,
 *   `grp` is not a role and needs no rows.
 *
 * The password is typed by the admin and handed over once — this v1 has no
 * invite flow (nothing in spec §10 mints one), so the honest UI is a password
 * field with the server's real minimum, not a fake "send invite" button.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, Checkbox, TextField } from '../../components/ui';
import { ApiRequestError } from '../../lib/api';
import { RoleAssignmentsEditor } from './role-editor';
import { errorSentence } from './states';
import { useCreateUser, useManageableCountries } from './use-admin-users';
import { MIN_PASSWORD_LENGTH } from './users-api';
import type { RoleAssignment } from './users-api';

export function UserCreatePage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { isGroupAdmin } = useSession();
  const manageable = useManageableCountries();
  const navigate = useNavigate();
  const create = useCreateUser();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [makeGroupAdmin, setMakeGroupAdmin] = useState(false);
  const [roles, setRoles] = useState<RoleAssignment[]>([]);
  /** Field-level messages appear on submit, not while typing. */
  const [errors, setErrors] = useState<Partial<Record<'name' | 'email' | 'password' | 'roles', string>>>({});

  const listHref = `/countries/${countryCode}/admin/users`;

  if (manageable.length === 0) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="User management is not available to this account">
          Creating users needs the users.manage permission in at least one country.
        </Alert>
      </div>
    );
  }

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (name.trim().length < 2) next.name = 'Enter the person’s full name.';
    if (!/^\S+@\S+\.\S+$/.test(email)) next.email = 'Enter a valid email address.';
    if (password.length < MIN_PASSWORD_LENGTH) {
      next.password = `The password needs at least ${String(MIN_PASSWORD_LENGTH)} characters.`;
    }
    if (!makeGroupAdmin && roles.length === 0) {
      next.roles =
        'Assign at least one country role — an account without one cannot enter any country.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (): void => {
    if (!validate() || create.isPending) return;
    create.mutate(
      {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() === '' ? undefined : phone.trim(),
        password,
        is_group_admin: makeGroupAdmin ? true : undefined,
        roles,
      },
      {
        onSuccess: (created) => {
          navigate(`/countries/${countryCode}/admin/users/${String(created.id)}`);
        },
        onError: (error) => {
          // 409 = the email is already an account; put it on the field it
          // belongs to rather than in the page-level alert.
          if (error instanceof ApiRequestError && error.code === 'CONFLICT') {
            setErrors((current) => ({
              ...current,
              email: 'An account with this email already exists.',
            }));
          }
        },
      },
    );
  };

  const showAlert =
    create.isError &&
    !(create.error instanceof ApiRequestError && create.error.code === 'CONFLICT');

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="New user"
        subtitle="The account can sign in as soon as it is created."
        actions={
          <Button variant="ghost" onClick={() => navigate(listHref)}>
            <ArrowLeft aria-hidden="true" className="size-4" />
            Back to users
          </Button>
        }
      />

      <form
        className="flex max-w-[560px] flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {showAlert ? (
          <Alert tone="danger" title="The account was not created">
            {errorSentence(create.error, 'The server refused the request. Nothing was saved.')}
          </Alert>
        ) : null}

        <TextField
          label="Full name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={errors.name}
          autoComplete="off"
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={errors.email}
          autoComplete="off"
        />
        <TextField
          label="Phone (optional)"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          autoComplete="off"
        />
        <TextField
          label={`Password (at least ${String(MIN_PASSWORD_LENGTH)} characters)`}
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={errors.password}
          autoComplete="new-password"
        />

        {isGroupAdmin ? (
          <Checkbox
            checked={makeGroupAdmin}
            onChange={(event) => setMakeGroupAdmin(event.target.checked)}
            label="Group administrator — every permission in every country"
          />
        ) : null}

        <div className="flex flex-col gap-2">
          <p className="text-label font-medium text-fg">
            {makeGroupAdmin ? 'Initial roles (optional for a group administrator)' : 'Initial roles'}
          </p>
          <RoleAssignmentsEditor value={roles} onChange={setRoles} countries={manageable} />
          {errors.roles === undefined ? null : (
            /* The Alert (danger TINT pair, 4.52:1 both modes), not bare
             * text-danger-fg: the status foregrounds are mode-invariant and
             * measure only 3.27:1 against the DARK page — they pass on their
             * own tint, so refusals here always sit on it. */
            <Alert tone="danger">{errors.roles}</Alert>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create user'}
          </Button>
          <Button variant="ghost" onClick={() => navigate(listHref)}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
